import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { ORIGINE_LABELS } from "../lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Building2, Phone, Mail, Pencil, X, BarChart3 } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { FornitoreStatsModal } from "../components/StatsModal";

export default function Fornitori() {
  const { text } = useSettings();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ nome: "", tipo: "mercato", posteggio: "", telefono: "", email: "", note: "" });
  const [editId, setEditId] = useState(null);
  const [statsId, setStatsId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/fornitori");
    setItems(data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const emptyForm = { nome: "", tipo: "mercato", posteggio: "", telefono: "", email: "", note: "" };
  const startEdit = (f) => { setEditId(f.id); setForm({ nome: f.nome, tipo: f.tipo, posteggio: f.posteggio || "", telefono: f.telefono || "", email: f.email || "", note: f.note || "" }); };
  const cancelEdit = () => { setEditId(null); setForm(emptyForm); };

  const create = async (e) => {
    e.preventDefault();
    if (!form.nome) { toast.error("Il nome del fornitore è obbligatorio"); return; }
    try {
      if (editId) { await api.put(`/fornitori/${editId}`, form); toast.success("Fornitore aggiornato"); }
      else { await api.post("/fornitori", form); toast.success("Fornitore aggiunto"); }
      cancelEdit();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/fornitori/${id}`); toast.success("Eliminato"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const input = "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 outline-none focus:border-primary";

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("fornitori_title")}</h1>
      <p className="text-slate-400 mb-6">{text("fornitori_subtitle")}</p>

      <form onSubmit={create} data-testid="fornitore-form" className="bg-slate-900/50 border border-slate-800 rounded-md p-5 mb-8 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input data-testid="fornitore-nome" placeholder="Nome / ragione sociale" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className={input} />
        <select data-testid="fornitore-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className={input}>
          <option value="mercato">Dal mercato</option>
          <option value="fuori_mercato">Fuori mercato</option>
        </select>
        <input data-testid="fornitore-telefono" placeholder="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={input} />
        <button data-testid="fornitore-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
          {editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editId ? "Aggiorna" : "Aggiungi"}
        </button>
        {editId && <button type="button" data-testid="fornitore-cancel" onClick={cancelEdit} className="md:col-span-4 text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="w-3 h-3" /> Annulla modifica</button>}
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fornitori-list">
        {items.length === 0 && <p className="text-slate-500 text-sm">Nessun fornitore.</p>}
        {items.map((f) => (
          <div key={f.id} data-testid={`fornitore-${f.id}`} className="bg-slate-900/50 border border-slate-800 rounded-md p-4 flex items-start gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${f.tipo === "fuori_mercato" ? "bg-amber-500/15" : "bg-purple-500/15"}`}>
              <Building2 className={`w-5 h-5 ${f.tipo === "fuori_mercato" ? "text-amber-400" : "text-purple-400"}`} />
            </div>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setStatsId(f.id)}>
              <div className="font-semibold text-slate-100 hover:text-primary transition-colors">{f.nome}</div>
              <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border mt-1 ${f.tipo === "fuori_mercato" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-purple-500/15 text-purple-300 border-purple-500/30"}`}>
                {ORIGINE_LABELS[f.tipo]}
              </span>
              {(f.telefono || f.email) && (
                <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                  {f.telefono && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {f.telefono}</div>}
                  {f.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {f.email}</div>}
                </div>
              )}
            </div>
            <button data-testid={`stats-fornitore-${f.id}`} onClick={() => setStatsId(f.id)} className="text-slate-500 hover:text-primary" title="Statistiche">
              <BarChart3 className="w-4 h-4" />
            </button>
            <button data-testid={`edit-fornitore-${f.id}`} onClick={() => startEdit(f)} className="text-slate-500 hover:text-primary">
              <Pencil className="w-4 h-4" />
            </button>
            <button data-testid={`delete-fornitore-${f.id}`} onClick={() => remove(f.id)} className="text-slate-500 hover:text-red-400">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {statsId && <FornitoreStatsModal id={statsId} onClose={() => setStatsId(null)} />}
    </div>
  );
}
